import { useCallback, useMemo } from 'react';
import {
  getAllCompositeSegments,
  getAllSegments,
  getClipFPS,
  isNotFullLengthClip,
  isOriginalLayoutAvailable,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import { getEditsForSegment, getScenesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import { hasCompositeSegmentBetweenSegmentIds } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/createClipValidation';
import { isAudioContent } from '@/stores/core';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { showErrorToast } from '@/libs/toast/toast';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';

const useCreateClip = ({ clip }: { clip: Clip }) => {
  const { createClip } = useTranscriptPage();

  const isCreateClipAllowed = useMemo(() => {
    if (isNotFullLengthClip(clip)) {
      return false;
    }
    return true;
  }, [clip]);

  const getDefaultLayout = useCallback(() => {
    if (isAudioContent()) {
      return 'AUDIOGRAM';
    }
    if (isOriginalLayoutAvailable(clip)) {
      return 'DEFAULT';
    }
    return 'GRID';
  }, [clip]);

  const onCreateClip = useCallback(
    (startTime: number | null, endTime: number | null, startSegmentId?: string, endSegmentId?: string) => {
      const hasMultipleScenes = (clip.asset_metadata.scenes || []).length > 1;
      if (
        isSplitsInTimelineEnabledPure({
          clip: clip,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }) &&
        startSegmentId &&
        endSegmentId
      ) {
        const additionalParams: Record<string, any> = {};
        const allSegments = getAllSegments(clip);

        const lastSegment = allSegments[allSegments.length - 1];
        const fps = getClipFPS(clip);
        const lastSegmentEndTime =
          lastSegment.type === SegmentType.COMPOSITE || lastSegment.type === SegmentType.VIDEO
            ? lastSegment.segment_metadata?.maxVideoDuration
            : lastSegment.toFrame / fps;

        if (startTime === null || endTime === null) {
          return Promise.reject(new Error('Start time or end time is null'));
        }

        const hasComposite = hasCompositeSegmentBetweenSegmentIds(allSegments, startSegmentId, endSegmentId);

        if (!hasComposite) {
          showErrorToast('At least one recording segment is required between the selected start and end time.');
          return Promise.reject(new Error('At least one composite segment is required'));
        }

        let clipDataWithStartTrim: Clip;
        try {
          clipDataWithStartTrim = updateClipData(
            {
              clipId: clip.id,
              actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
              startTime: endTime,
              startSegmentId: endSegmentId || '',
              endSegmentId: lastSegment.id || '',
              endTime: lastSegmentEndTime || 0,
              deleteType: 'MANUAL'
            },
            {
              currentClipData: updateClipData(
                {
                  clipId: clip.id,
                  actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
                  startTime: 0,
                  startSegmentId: allSegments[0].id,
                  endSegmentId: startSegmentId || '',
                  endTime: startTime,
                  deleteType: 'MANUAL'
                },
                {
                  skipStoreAndBackendUpdate: true
                }
              ),
              skipStoreAndBackendUpdate: true
            }
          ) as Clip;
        } catch (err) {
          return Promise.reject(err);
        }

        let clipDataWithEndAndStartTrim: Clip;
        try {
          clipDataWithEndAndStartTrim = updateClipData(
            {
              clipId: clip.id,
              actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
              layout: getDefaultLayout()
            },
            {
              currentClipData: clipDataWithStartTrim,
              skipStoreAndBackendUpdate: true
            }
          ) as Clip;
        } catch (err) {
          return Promise.reject(err);
        }
        additionalParams.player_config = clipDataWithEndAndStartTrim.asset_metadata.player_config;
        additionalParams.layout = hasMultipleScenes ? clip.asset_metadata.layout : getDefaultLayout();
        additionalParams.duration = clipDataWithEndAndStartTrim.asset_metadata.duration;
        additionalParams.start = clipDataWithEndAndStartTrim.asset_metadata.start;
        additionalParams.end = clipDataWithEndAndStartTrim.asset_metadata.end;
        additionalParams.captions = clipDataWithEndAndStartTrim.asset_metadata.captions;

        const allSegmentAfterEndSegment = getAllCompositeSegments(clipDataWithEndAndStartTrim);
        const minStartTime =
          allSegmentAfterEndSegment.length > 1
            ? Math.min(...allSegmentAfterEndSegment.map(seg => seg.segment_metadata?.start || 0))
            : clipDataWithEndAndStartTrim.asset_metadata.start;
        const maxEndTime =
          allSegmentAfterEndSegment.length > 1
            ? Math.max(...allSegmentAfterEndSegment.map(seg => seg.segment_metadata?.end || 0))
            : clipDataWithEndAndStartTrim.asset_metadata.end;

        return createClip(minStartTime, maxEndTime, {
          edits: clipDataWithEndAndStartTrim.asset_metadata.edits,
          deletes: clipDataWithEndAndStartTrim.asset_metadata.deletes || {},
          scenes: undefined,
          fps,
          additionalParams
        });
      }

      if (startTime === null || endTime === null) {
        return Promise.reject(new Error('Start time or end time is null'));
      }

      let clipDataWithUpdatedLayout: Clip;
      try {
        clipDataWithUpdatedLayout = hasMultipleScenes
          ? clip
          : (updateClipData(
              {
                clipId: clip.id,
                actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
                layout: getDefaultLayout()
              },
              {
                skipStoreAndBackendUpdate: true
              }
            ) as Clip);
      } catch (err) {
        return Promise.reject(err);
      }
      return createClip(startTime, endTime, {
        edits: getEditsForSegment(clipDataWithUpdatedLayout),
        deletes: getDeletesForSegmentPure({
          clip: clipDataWithUpdatedLayout,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        scenes: getScenesForSegment(clipDataWithUpdatedLayout),
        fps: getClipFPS(clip),
        additionalParams: {
          layout: hasMultipleScenes ? clip.asset_metadata.layout : getDefaultLayout()
        }
      });
    },
    [clip, createClip, getDefaultLayout]
  );

  return { isCreateClipAllowed, onCreateClip };
};

export default useCreateClip;
